/**
 * @spec docs/specs/features/staking-ui-batched-reads.md (R1, R3, R5, R8)
 *
 * Client for rewards-api's batched public reads. Amounts arrive as decimal strings and leave as
 * bigint; a value the server could not read is null, never 0. Each value's age is carried as
 * `servedAt − observedAt` (both server timestamps), so callers never compare the client clock
 * with a server timestamp.
 */
import { env } from './env';

export type ReadChain = 'base' | 'polygon';
export type BaseAsset = 'phnm' | 'usdc' | 'chip' | 'eth';
export type PolygonAsset = 'usdt' | 'pol';
export type BalanceAsset = BaseAsset | PolygonAsset;

export const BASE_ASSETS: readonly BaseAsset[] = ['phnm', 'usdc', 'chip', 'eth'];
export const POLYGON_ASSETS: readonly PolygonAsset[] = ['usdt', 'pol'];

export function chainOfAsset(asset: BalanceAsset): ReadChain {
  return asset === 'usdt' || asset === 'pol' ? 'polygon' : 'base';
}

/** One value as the client holds it. */
export interface Observation {
  readonly value: bigint | null;
  /** The server served a last-known value (refresh failed, over budget, or below the floor). */
  readonly stale: boolean;
  readonly blockNumber: bigint | null;
  /** Age of the value when served: `servedAt − observedAt`, server clock only. */
  readonly ageAtServeMs: number | null;
}

export interface AddressBalances {
  readonly base: Readonly<Record<BaseAsset, Observation>>;
  readonly polygon: Readonly<Record<PolygonAsset, Observation>>;
}

export interface BalancesResponse {
  /** Keyed by lower-cased address. */
  readonly byAddress: Readonly<Record<string, AddressBalances>>;
}

export interface BalancesRequestOptions {
  readonly minBlock?: { base?: number; polygon?: number };
  /** Chains to read uncached; sent as `{ base?: true, polygon?: true }`, omitted when empty. */
  readonly bypassCache?: Partial<Record<ReadChain, boolean>>;
}

/** A failed public-read call; `code` is the server's stable error code when it sent one. */
export class PublicReadError extends Error {
  readonly status: number;
  readonly code: string | null;
  constructor(status: number, code: string | null) {
    super(`Public read failed (${code ?? `HTTP ${status}`})`);
    this.name = 'PublicReadError';
    this.status = status;
    this.code = code;
  }
}

interface WireValue {
  value?: unknown;
  stale?: unknown;
  blockNumber?: unknown;
  observedAt?: unknown;
}

const UNREAD: Observation = { value: null, stale: true, blockNumber: null, ageAtServeMs: null };

function apiUrl(path: string): string {
  const base = env.rewardsApiUrl;
  if (!base) throw new Error('Rewards API URL not configured');
  return `${base.replace(/\/$/, '')}${path}`;
}

function toBigInt(raw: unknown): bigint | null {
  return typeof raw === 'string' && /^\d+$/.test(raw) ? BigInt(raw) : null;
}

function toTime(raw: unknown): number | null {
  if (typeof raw !== 'string') return null;
  const ms = Date.parse(raw);
  return Number.isNaN(ms) ? null : ms;
}

/** Parse one wire value. Anything malformed is unavailable, never a number. */
export function parseObservation(raw: unknown, servedAtMs: number | null): Observation {
  if (typeof raw !== 'object' || raw === null) return UNREAD;
  const wire = raw as WireValue;
  const value = toBigInt(wire.value);
  const observedAt = toTime(wire.observedAt);
  if (value === null || observedAt === null || servedAtMs === null) return UNREAD;
  return {
    value,
    stale: wire.stale !== false,
    blockNumber: toBigInt(wire.blockNumber),
    ageAtServeMs: Math.max(0, servedAtMs - observedAt),
  };
}

/** The server's refusal of a `minBlock` above its head: that floor can never be met. */
export function isMinBlockRejected(error: unknown): boolean {
  return (
    error instanceof PublicReadError &&
    error.status === 400 &&
    error.code === 'MIN_BLOCK_AHEAD_OF_HEAD'
  );
}

async function readJson(response: Response): Promise<unknown> {
  const body: unknown = await response.json().catch(() => null);
  if (!response.ok) {
    const code =
      typeof body === 'object' &&
      body !== null &&
      typeof (body as { error?: unknown }).error === 'string'
        ? (body as { error: string }).error
        : null;
    throw new PublicReadError(response.status, code);
  }
  return body;
}

function section<A extends string>(
  raw: unknown,
  assets: readonly A[],
  servedAtMs: number | null
): Record<A, Observation> {
  const record = (typeof raw === 'object' && raw !== null ? raw : {}) as Record<string, unknown>;
  const out = {} as Record<A, Observation>;
  for (const asset of assets) out[asset] = parseObservation(record[asset], servedAtMs);
  return out;
}

/** `POST /read/balances` for 1..8 addresses. */
export async function fetchBalances(
  addresses: readonly string[],
  options: BalancesRequestOptions = {},
  signal?: AbortSignal
): Promise<BalancesResponse> {
  const body: Record<string, unknown> = { addresses };
  if (options.minBlock && Object.keys(options.minBlock).length > 0)
    body.minBlock = options.minBlock;
  const bypass = Object.fromEntries(
    Object.entries(options.bypassCache ?? {}).filter(([, wanted]) => wanted === true)
  );
  if (Object.keys(bypass).length > 0) body.bypassCache = bypass;

  const response = await fetch(apiUrl('/read/balances'), {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
    signal,
  });
  const json = (await readJson(response)) as { servedAt?: unknown; addresses?: unknown };
  const servedAtMs = toTime(json.servedAt);
  const byAddress: Record<string, AddressBalances> = {};
  const rows = Array.isArray(json.addresses) ? json.addresses : [];
  for (const row of rows as { address?: unknown; base?: unknown; polygon?: unknown }[]) {
    if (typeof row?.address !== 'string') continue;
    byAddress[row.address.toLowerCase()] = {
      base: section(row.base, BASE_ASSETS, servedAtMs),
      polygon: section(row.polygon, POLYGON_ASSETS, servedAtMs),
    };
  }
  return { byAddress };
}

/** `GET /read/global`: Home supply stats and swap-pool balances. */
export interface GlobalStatsResponse {
  readonly supply: {
    readonly totalSupply: Observation;
    readonly stakedBalance: Observation;
    readonly circulatingSupply: Observation;
    readonly stakingRatioPercent: number | null;
    readonly repositoryBalance: Observation;
    readonly totalPositionNFTs: Observation;
    readonly treasuryBalance: Observation;
    readonly treasuryStakedBalance: Observation;
    readonly treasuryTotalBalance: Observation;
    readonly treasuryAddresses: readonly string[];
  };
  readonly pool: { readonly phnm: Observation; readonly usdc: Observation };
}

const SUPPLY_AMOUNTS = [
  'totalSupply',
  'stakedBalance',
  'circulatingSupply',
  'repositoryBalance',
  'totalPositionNFTs',
  'treasuryBalance',
  'treasuryStakedBalance',
  'treasuryTotalBalance',
] as const;

export async function fetchGlobalStats(signal?: AbortSignal): Promise<GlobalStatsResponse> {
  const json = (await readJson(await fetch(apiUrl('/read/global'), { signal }))) as {
    servedAt?: unknown;
    supply?: Record<string, unknown>;
    pool?: Record<string, unknown>;
  };
  const servedAtMs = toTime(json.servedAt);
  const supply = json.supply ?? {};
  const ratio = (supply.stakingRatioPercent ?? null) as { value?: unknown } | null;
  const amounts = section(supply, SUPPLY_AMOUNTS, servedAtMs);
  return {
    supply: {
      ...amounts,
      stakingRatioPercent: typeof ratio?.value === 'number' ? ratio.value : null,
      treasuryAddresses: Array.isArray(supply.treasuryAddresses)
        ? supply.treasuryAddresses.filter((a): a is string => typeof a === 'string')
        : [],
    },
    pool: section(json.pool, ['phnm', 'usdc'] as const, servedAtMs),
  };
}

/** `GET /read/chip-redemptions/:address`: advisory CHIP total already redeemed. */
export interface ChipRedemptionsResponse {
  /**
   * The total as an observation: `value` is null when unavailable or when the history is too
   * large to total; `stale`, `blockNumber` and the age are read like a balance's.
   */
  readonly total: Observation;
}

export interface ChipRedemptionsRequestOptions {
  readonly minBlock?: number;
  readonly bypassCache?: boolean;
}

export async function fetchChipRedemptions(
  address: string,
  options: ChipRedemptionsRequestOptions = {},
  signal?: AbortSignal
): Promise<ChipRedemptionsResponse> {
  const params = new URLSearchParams();
  if (options.minBlock !== undefined) params.set('minBlock', String(options.minBlock));
  if (options.bypassCache) params.set('bypassCache', '1');
  const search = params.toString();
  const query = search ? `?${search}` : '';
  const url = apiUrl(`/read/chip-redemptions/${encodeURIComponent(address)}${query}`);
  const json = (await readJson(await fetch(url, { signal }))) as {
    servedAt?: unknown;
    total?: unknown;
    tooLarge?: unknown;
    stale?: unknown;
    blockNumber?: unknown;
    observedAt?: unknown;
  };
  if (json.tooLarge === true) {
    // Too many redemptions to total: unknown, but still a real answer at its snapshot block.
    return {
      total: {
        value: null,
        stale: json.stale !== false,
        blockNumber: toBigInt(json.blockNumber),
        ageAtServeMs: null,
      },
    };
  }
  const wire: WireValue = {
    value: json.total,
    stale: json.stale,
    blockNumber: json.blockNumber,
    observedAt: json.observedAt,
  };
  return { total: parseObservation(wire, toTime(json.servedAt)) };
}
